'use client';

import { type VariantProps } from 'class-variance-authority';
import {
  type HTMLAttributes,
  isValidElement,
  type ReactNode,
  type Ref,
  useMemo,
} from 'react';

import { Icon, type IconType } from '@/app/components/icons';
import { IconExpandMore } from '@/app/components/icons/ui-actions/icon-expand-more';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';

const AccordionStyled = cnva(
  [
    'text-sub-h3 font-noto-bold transition-form relative flex w-full cursor-pointer gap-x-2 p-4 text-left select-none',
    'focus-visible:outline-none',
  ],
  {
    variants: {
      theme: {
        light: 'bg-white hover:bg-gray-50 focus-visible:bg-gray-50',
        dark: 'bg-gray-900 text-white hover:bg-gray-700 focus-visible:bg-gray-700',
      },
      isOpen: {
        true: 'text-red-500',
        false: '',
      },
    },
    defaultVariants: {
      theme: 'light',
      isOpen: false,
    },
  }
);

const AccordionContentStyled = cnva(
  ['where-ul:list-ul relative p-4 [&>div]:space-y-2'],
  {
    variants: {
      theme: {
        light: 'where-strong:text-gray-900 bg-white text-gray-700',
        dark: 'where-strong:text-white bg-gray-900 text-gray-50',
      },
      isOpen: {
        true: '',
        false: '',
      },
      withDescription: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        isOpen: false,
        withDescription: false,
        className: 'hidden',
      },
      {
        isOpen: false,
        withDescription: true,
        className: '*:line-clamp-4 *:h-24',
      },
      {
        isOpen: true,
        withDescription: true,
        className: '*:space-y-4',
      },
    ],
    defaultVariants: {
      theme: 'light',
      isOpen: false,
      withDescription: false,
    },
  }
);

export interface AccordionProps {
  /**
   * (Optional) The ref for the accordion
   */
  ref?: Ref<HTMLDivElement>;

  /**
   * (Optional) The class name for the wrapper of the accordion
   */
  wrapperClassName?: string;

  /**
   * (Optional) The class name for the accordion
   */
  className?: string;

  /**
   * (Optional) The class name for the content of the accordion
   */
  contentClassName?: string;

  /**
   * (Optional) The class name for the expand icon
   */
  iconClassName?: string;

  /**
   * (Optional) The theme of the accordion
   */
  theme?: VariantProps<typeof AccordionStyled>['theme'];

  /**
   * (Optional) The title of the accordion
   */
  title?: string | ReactNode;

  /**
   * (Optional) The icon to display
   */
  leftIcon?: ReactNode;

  /**
   * The content (can be html) to display inside the accordion
   */
  content: ReactNode;

  /**
   * (Optional) Whether to show the description
   */
  withDescription?: boolean;

  /**
   * (Optional) The callback function to handle the change event
   */
  onChange?: (id: string, isOpen: boolean) => void;

  /**
   * The value of the accordion
   */
  value: string;

  /**
   * (Optional) Whether the accordion is open
   */
  isOpen?: boolean;

  /**
   * (Optional) Custom data-test value. Default is `accordion`.
   */
  test?: string;

  /**
   * (Optional) The props for the wrapper
   */
  wrapperProps?: HTMLAttributes<HTMLDivElement>;
}

export const Accordion = ({
  ref,
  wrapperClassName,
  className,
  contentClassName,
  iconClassName,
  theme,
  title,
  content,
  leftIcon = null,
  withDescription = false,
  value,
  isOpen = false,
  test = 'accordion',
  wrapperProps,
  onChange,
  ...props
}: AccordionProps) => {
  const handleChange = (value: string) => {
    safeCall(onChange, value, !isOpen);
  };

  const isIconType = useMemo(() => {
    if (!isValidElement(leftIcon)) return;
    const functionName = (
      leftIcon.type?.valueOf() as unknown as { name: string }
    )?.name;
    return (
      typeof leftIcon.type === 'function' && functionName === 'IconSettings'
    );
  }, [leftIcon]);

  return (
    <div
      ref={ref}
      className={wrapperClassName}
      {...createDataAttributes({
        test,
      })}
      {...wrapperProps}
    >
      <button
        type="button"
        id={`${value}-title`}
        className={AccordionStyled({
          theme,
          className,
          isOpen,
        })}
        onClick={() => handleChange(value)}
        aria-expanded={isOpen}
        aria-controls={`${value}-content`}
        {...createDataAttributes({
          test: 'accordion-title',
        })}
        {...props}
      >
        {!!leftIcon &&
          (isIconType ? (
            <Icon className="pointer-events-none" icon={leftIcon as IconType} />
          ) : (
            leftIcon
          ))}
        {!!title && title}
        <Icon
          icon={<IconExpandMore />}
          className={cn(
            'pointer-events-none ml-auto transition-transform duration-300',
            {
              'rotate-180': isOpen,
            },
            iconClassName
          )}
        />
      </button>
      <div
        id={`${value}-content`}
        role="region"
        aria-labelledby={`${value}-title`}
        className={AccordionContentStyled({
          theme,
          isOpen,
          withDescription,
          className: contentClassName,
        })}
        {...createDataAttributes({
          test: 'accordion-content',
        })}
      >
        {content}
      </div>
    </div>
  );
};
